import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const CANCELLATION_REASONS = [
  { 
    id: 'too_expensive', 
    label: 'Too expensive',
    question: 'What price would work for you?'
  },
  { 
    id: 'not_using', 
    label: 'Not using it enough',
    question: 'What would help you use it more?'
  },
  { 
    id: 'found_alternative', 
    label: 'Found a better alternative',
    question: 'What does it offer that we don\'t?'
  },
  { 
    id: 'missing_features', 
    label: 'Missing features I need',
    question: 'Which features would you like?'
  },
  { 
    id: 'technical_issues', 
    label: 'Technical issues',
    question: 'What issues did you experience?'
  },
  { 
    id: 'other', 
    label: 'Other',
    question: 'Please share your reason'
  }
];

export default function CancellationFeedbackModal({ open, onClose, onConfirm }) {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [reasonDetails, setReasonDetails] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleDetailChange = (reasonId, value) => {
    setReasonDetails(prev => ({
      ...prev,
      [reasonId]: value
    }));
  };

  const handleSkipAndCancel = async () => {
    setSubmitting(true);
    try {
      await onConfirm(null); // No feedback provided
    } catch (error) {
      toast.error('Cancellation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAndCancel = async () => {
    setSubmitting(true);
    try {
      // Build detailed feedback from reason-specific answers
      const detailsArray = Object.entries(reasonDetails)
        .filter(([reasonId, detail]) => selectedReasons.includes(reasonId) && detail?.trim())
        .map(([reasonId, detail]) => {
          const reason = CANCELLATION_REASONS.find(r => r.id === reasonId);
          return `${reason.label}: ${detail}`;
        });
      
      const feedback = {
        reasons: selectedReasons.length > 0 ? selectedReasons : null,
        additional_feedback: detailsArray.join('\n\n') || null
      };
      
      await onConfirm(feedback);
    } catch (error) {
      console.error('Submit error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Cancellation failed';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl">We're sorry to see you go! 😢</DialogTitle>
          <DialogDescription className="text-base pt-2">
            Before you go, would you mind helping us improve? Your feedback is completely optional.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-base font-semibold mb-3 block">
              Why are you cancelling? (optional)
            </Label>
            <div className="space-y-3">
              {CANCELLATION_REASONS.map(reason => (
                <div key={reason.id} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={reason.id}
                      checked={selectedReasons.includes(reason.id)}
                      onCheckedChange={() => handleReasonToggle(reason.id)}
                    />
                    <label
                      htmlFor={reason.id}
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      {reason.label}
                    </label>
                  </div>
                  
                  {/* Show specific input when selected */}
                  {selectedReasons.includes(reason.id) && (
                    <div className="ml-6 animate-in slide-in-from-top-2 duration-200">
                      <Input
                        placeholder={reason.question}
                        value={reasonDetails[reason.id] || ''}
                        onChange={(e) => handleDetailChange(reason.id, e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleSkipAndCancel}
            disabled={submitting}
            className="flex-1"
          >
            Skip & Cancel
          </Button>
          <Button
            onClick={handleSubmitAndCancel}
            disabled={submitting}
            className="flex-1 bg-[#3477f8] hover:bg-[#2563eb]"
          >
            {submitting ? 'Processing...' : 'Submit & Cancel'}
          </Button>
        </div>

        <p className="text-xs text-gray-500 text-center pt-2">
          You'll keep access until the end of your billing period
        </p>
      </DialogContent>
    </Dialog>
  );
}
